import { FC } from 'react';
import { Check, CloudOff, Loader2, AlertTriangle } from 'lucide-react';
import type { AutosaveStatus } from '@/hooks/useAutosave';

interface AutosaveIndicatorProps {
  status: AutosaveStatus;
  lastSavedAt: number | null;
  className?: string;
}

function formatTime(savedAt: number): string {
  return new Date(savedAt).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Compact status line for the autosave state. Sits next to the save button so
 * the user can tell at a glance whether their typing is being held locally.
 */
const AutosaveIndicator: FC<AutosaveIndicatorProps> = ({ status, lastSavedAt, className = '' }) => {
  if (status === 'idle' && !lastSavedAt) return null;

  const base = `flex items-center gap-1.5 text-xs ${className}`;

  if (status === 'saving') {
    return (
      <span className={`${base} text-gray-500 dark:text-gray-400`} data-testid="autosave-indicator">
        <Loader2 size={13} className="animate-spin" />
        Menyimpan draft...
      </span>
    );
  }

  if (status === 'offline') {
    return (
      <span className={`${base} text-amber-600 dark:text-amber-400`} data-testid="autosave-indicator">
        <CloudOff size={13} />
        Offline — tersimpan lokal{lastSavedAt ? ` ${formatTime(lastSavedAt)}` : ''}
      </span>
    );
  }

  if (status === 'error') {
    return (
      <span className={`${base} text-red-600 dark:text-red-400`} data-testid="autosave-indicator">
        <AlertTriangle size={13} />
        Draft gagal disimpan di perangkat ini
      </span>
    );
  }

  if (status === 'saved' && lastSavedAt) {
    return (
      <span className={`${base} text-gray-500 dark:text-gray-400`} data-testid="autosave-indicator">
        <Check size={13} className="text-green-600 dark:text-green-500" />
        Draft tersimpan {formatTime(lastSavedAt)}
      </span>
    );
  }

  return null;
};

export default AutosaveIndicator;
